/**
 * Shared helpers for the storage-location hierarchy ("lager/hylleplassering").
 * Kept separate from lib/api so both the sidebar tree, the location picker,
 * and the bulk-move modal can share one source of truth for icon/label
 * mapping and tree-flattening instead of re-deriving it three times.
 */
import type { IconName } from '@/design-system';
import type { StorageLocation, StorageLocationKind, StorageLocationTreeNode } from '@/lib/api';

export const LOCATION_KIND_ICON: Record<StorageLocationKind, IconName> = {
  room: 'door',
  cabinet: 'folder',
  fridge: 'fridge',
  shelf: 'rack',
  freezer: 'snow',
  other: 'archive',
};

export const LOCATION_KINDS: StorageLocationKind[] = ['room', 'cabinet', 'fridge', 'shelf', 'freezer', 'other'];

export interface FlatLocationOption {
  id: string;
  name: string;
  kind: StorageLocationKind;
  depth: number;
  parentId?: string;
}

/** Flattens the tree into a depth-first list with indentation depth — feeds <Select> option lists. */
export function flattenLocationTree(nodes: StorageLocationTreeNode[], depth = 0): FlatLocationOption[] {
  const out: FlatLocationOption[] = [];
  for (const node of [...nodes].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))) {
    out.push({ id: node.id, name: node.name, kind: node.kind, depth, parentId: node.parentId });
    out.push(...flattenLocationTree(node.children, depth + 1));
  }
  return out;
}

/** Same flattening but from a flat `StorageLocation[]` (e.g. GET /locations) rather than the tree endpoint. */
export function flattenLocationList(locations: StorageLocation[]): FlatLocationOption[] {
  const byParent = new Map<string, StorageLocation[]>();
  for (const location of locations) {
    const key = location.parentId ?? '__root__';
    byParent.set(key, [...(byParent.get(key) ?? []), location]);
  }
  const walk = (parentKey: string, depth: number): FlatLocationOption[] => {
    const siblings = (byParent.get(parentKey) ?? []).sort(
      (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
    );
    return siblings.flatMap((location) => [
      { id: location.id, name: location.name, kind: location.kind, depth, parentId: location.parentId },
      ...walk(location.id, depth + 1),
    ]);
  };
  return walk('__root__', 0);
}

/** A node + every id in its subtree — used when a Room/Cabinet is selected in the sidebar to filter the whole branch. */
export function collectDescendantIds(node: StorageLocationTreeNode): string[] {
  return [node.id, ...node.children.flatMap(collectDescendantIds)];
}

/** Depth-first search for a node by id anywhere in the tree. */
export function findLocationNode(nodes: StorageLocationTreeNode[], id: string): StorageLocationTreeNode | undefined {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findLocationNode(node.children, id);
    if (found) return found;
  }
  return undefined;
}

export const MAX_LOCATION_DEPTH = 3;
